import { useState } from 'react';
import { useChatStore } from '../../store/chatStore';
import { useFeedback } from '../../hooks/useFeedback';
import { CorrectionModal } from './CorrectionModal';
import { SuggestEditModal } from './SuggestEditModal';
import { ThumbsUp, ThumbsDown, MessageSquare, Copy, Check, FileEdit, ListPlus } from 'lucide-react';

interface FeedbackButtonsProps {
  messageId: string;
  currentFeedback?: 'positive' | 'negative' | 'need_more_detail' | null;
}

export function FeedbackButtons({ messageId, currentFeedback }: FeedbackButtonsProps) {
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [showSuggestEditModal, setShowSuggestEditModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const { setMessageFeedback, messages, persona } = useChatStore();
  const { submitFeedback } = useFeedback();

  const message = messages.find((m) => m.id === messageId);

  // Only show "Suggest Edit" for developer persona
  const showDevTools = persona === 'developer';

  const handleFeedback = async (type: 'positive' | 'negative' | 'need_more_detail') => {
    const newFeedback = currentFeedback === type ? null : type;
    setMessageFeedback(messageId, newFeedback);

    if (newFeedback && message) {
      // Find the user question that preceded this response
      const messageIndex = messages.findIndex((m) => m.id === messageId);
      const userMessage = messages.slice(0, messageIndex).reverse().find((m) => m.role === 'user');

      await submitFeedback({
        messageId,
        query: userMessage?.content || '',
        wasHelpful: type === 'positive',
        rating: type === 'positive' ? 5 : type === 'need_more_detail' ? 3 : 1,
        feedbackType: type,
      });
    }

    // Show correction modal for negative feedback
    if (type === 'negative' && newFeedback === 'negative') {
      setShowCorrectionModal(true);
    }
  };

  const handleCopy = async () => {
    if (message) {
      // Find the user question that preceded this response
      const messageIndex = messages.findIndex((m) => m.id === messageId);
      const userMessage = messages.slice(0, messageIndex).reverse().find((m) => m.role === 'user');

      // Build copy text with question, response, and source file names
      let copyText = '';

      if (userMessage) {
        copyText += `Question: ${userMessage.content}\n\n`;
      }

      copyText += `Answer: ${message.content}`;

      if (message.sources && message.sources.length > 0) {
        const sourceNames = message.sources.map((s) => {
          if (s.path) {
            const filename = s.path.split('/').pop() || s.path;
            if (filename && filename.toLowerCase() !== 'document') return filename;
          }
          if (s.title && s.title.toLowerCase() !== 'document') return s.title;
          return s.path || s.title || 'Unknown';
        });
        copyText += `\n\nSources: ${sourceNames.join(', ')}`;
      }

      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <div className="flex items-center gap-1 mt-3">
        {/* Thumbs up */}
        <button
          onClick={() => handleFeedback('positive')}
          className={`p-1.5 rounded-lg transition-colors ${
            currentFeedback === 'positive'
              ? 'bg-green-100 text-green-600'
              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
          }`}
          aria-label="Mark as helpful"
          aria-pressed={currentFeedback === 'positive'}
        >
          <ThumbsUp size={16} />
        </button>

        {/* Thumbs down */}
        <button
          onClick={() => handleFeedback('negative')}
          className={`p-1.5 rounded-lg transition-colors ${
            currentFeedback === 'negative'
              ? 'bg-red-100 text-red-600'
              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
          }`}
          aria-label="Mark as not helpful"
          aria-pressed={currentFeedback === 'negative'}
        >
          <ThumbsDown size={16} />
        </button>

        {/* I need more detail */}
        <button
          onClick={() => handleFeedback('need_more_detail')}
          className={`p-1.5 rounded-lg transition-colors ${
            currentFeedback === 'need_more_detail'
              ? 'bg-blue-100 text-blue-600'
              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
          }`}
          aria-label="I need more detail"
          aria-pressed={currentFeedback === 'need_more_detail'}
          title="I need more detail"
        >
          <ListPlus size={16} />
        </button>

        {/* Divider */}
        <div className="w-px h-4 bg-gray-200 mx-1" />

        {/* Correct answer button */}
        <button
          onClick={() => setShowCorrectionModal(true)}
          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          aria-label="Provide correction"
          title="Correct this answer"
        >
          <MessageSquare size={16} />
        </button>

        {/* Suggest Edit button (developer only) */}
        {showDevTools && (
          <button
            onClick={() => setShowSuggestEditModal(true)}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-blue-600 transition-colors"
            aria-label="Suggest documentation edit"
            title="Suggest documentation edit"
          >
            <FileEdit size={16} />
          </button>
        )}

        {/* Copy button */}
        <button
          onClick={handleCopy}
          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          aria-label="Copy response"
          title="Copy to clipboard"
        >
          {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
        </button>
      </div>

      {/* Correction modal */}
      {showCorrectionModal && (
        <CorrectionModal
          messageId={messageId}
          onClose={() => setShowCorrectionModal(false)}
        />
      )}

      {/* Suggest Edit modal (developer only) */}
      {showSuggestEditModal && message && (
        <SuggestEditModal
          messageId={messageId}
          sources={message.sources}
          originalContent={message.content}
          onClose={() => setShowSuggestEditModal(false)}
        />
      )}
    </>
  );
}
